/**
 * Admin — Onboarding Questions
 * Manages the triage "Quick Questions" new members answer during onboarding:
 * create/edit/delete, activate/deactivate, and drag-to-reorder. Backed by the
 * admin-diagnostic edge function's /triage-questions endpoints.
 *
 * No page-level auth gate: the /admin layout gates access and the edge
 * function re-checks server-side.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { OnboardingQuestionEditorSheet } from '@/components/admin/onboarding/OnboardingQuestionEditorSheet';
import { readTriageOptions } from '@/lib/triage-options';
import {
  apiErrorMessage,
  TRIAGE_QUESTION_TYPES,
  useAdminTriageQuestions,
  useDeleteTriageQuestion,
  useReorderTriageQuestions,
  useUpdateTriageQuestion,
  type AdminTriageQuestion,
} from '@/hooks/use-admin-triage';

export const Route = createFileRoute('/admin/onboarding-questions')({
  component: OnboardingQuestionsAdmin,
});

const TYPE_LABEL = Object.fromEntries(
  TRIAGE_QUESTION_TYPES.map((type) => [type.value, type.label]),
) as Record<string, string>;

function OnboardingQuestionsAdmin() {
  const questionsQuery = useAdminTriageQuestions();
  const updateMutation = useUpdateTriageQuestion();
  const deleteMutation = useDeleteTriageQuestion();
  const reorderMutation = useReorderTriageQuestions();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AdminTriageQuestion | null>(null);
  const [deleting, setDeleting] = useState<AdminTriageQuestion | null>(null);

  const questions = questionsQuery.data ?? [];
  const activeCount = questions.filter((question) => question.is_active).length;

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (question: AdminTriageQuestion) => {
    setEditing(question);
    setEditorOpen(true);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;

    const next = Array.from(questions);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);

    reorderMutation.mutate(
      next.map((question) => question.id),
      { onError: (error) => toast.error(apiErrorMessage(error)) },
    );
  };

  const toggleActive = (question: AdminTriageQuestion, isActive: boolean) => {
    updateMutation.mutate(
      { id: question.id, input: { is_active: isActive } },
      {
        onSuccess: () =>
          toast.success(isActive ? `${question.code} is live again` : `${question.code} hidden from onboarding`),
        onError: (error) => toast.error(apiErrorMessage(error)),
      },
    );
  };

  // Questions with member responses can't be hard-deleted (server enforces
  // with a 409) — the confirm dialog offers deactivation instead.
  const deletingHasResponses = (deleting?.response_count ?? 0) > 0;

  const confirmDelete = () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    if (deletingHasResponses) {
      updateMutation.mutate(
        { id: target.id, input: { is_active: false } },
        {
          onSuccess: () => toast.success(`${target.code} deactivated`),
          onError: (error) => toast.error(apiErrorMessage(error)),
        },
      );
    } else {
      deleteMutation.mutate(target.id, {
        onSuccess: () => toast.success(`${target.code} deleted`),
        onError: (error) => toast.error(apiErrorMessage(error)),
      });
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] md:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Onboarding Questions</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              The Quick Questions new members answer during onboarding. Drag to reorder —
              changes go live immediately.
            </p>
          </div>
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Add question
          </Button>
        </div>

        {/* Content */}
        {questionsQuery.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : questionsQuery.isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              Couldn't load onboarding questions: {apiErrorMessage(questionsQuery.error)}
            </AlertDescription>
          </Alert>
        ) : questions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-16 text-center">
            <p className="text-sm text-muted-foreground">
              No onboarding questions yet. Add the first one.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              {activeCount} of {questions.length} questions shown to new members
            </p>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="onboarding-questions">
                {(droppable) => (
                  <div ref={droppable.innerRef} {...droppable.droppableProps} className="space-y-2">
                    {questions.map((question, index) => (
                      <Draggable key={question.id} draggableId={question.id} index={index}>
                        {(draggable, snapshot) => (
                          <div
                            ref={draggable.innerRef}
                            {...draggable.draggableProps}
                            className={`flex items-center gap-2 rounded-lg border border-border bg-card p-3 transition-shadow ${
                              snapshot.isDragging ? 'shadow-lg ring-1 ring-primary/40' : ''
                            } ${question.is_active ? '' : 'opacity-60'}`}
                          >
                            <span
                              {...draggable.dragHandleProps}
                              className="cursor-grab p-1 text-muted-foreground transition-colors hover:text-foreground active:cursor-grabbing"
                              aria-label={`Reorder ${question.code}`}
                            >
                              <GripVertical className="h-4 w-4" />
                            </span>
                            <span className="w-6 text-center font-mono text-xs text-muted-foreground">
                              {index + 1}
                            </span>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">{question.question_text}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                                <code className="rounded bg-secondary px-1.5 py-0.5 font-mono text-[11px]">
                                  {question.code}
                                </code>
                                <Badge variant="secondary" className="font-normal">
                                  {TYPE_LABEL[question.type] ?? question.type}
                                </Badge>
                                {(question.type === 'select' || question.type === 'multiselect') && (
                                  <span>{readTriageOptions(question.options).labels.length} options</span>
                                )}
                                {question.type === 'numeric' && (() => {
                                  const { min, max } = readTriageOptions(question.options);
                                  if (min === undefined && max === undefined) return null;
                                  return (
                                    <span>
                                      {min ?? 0}–{max ?? '∞'}
                                    </span>
                                  );
                                })()}
                                {question.response_count > 0 && (
                                  <span>
                                    {question.response_count} answer{question.response_count === 1 ? '' : 's'}
                                  </span>
                                )}
                                {!question.is_active && (
                                  <Badge variant="outline" className="font-normal">
                                    Inactive
                                  </Badge>
                                )}
                              </div>
                            </div>

                            <Switch
                              checked={question.is_active}
                              onCheckedChange={(checked) => toggleActive(question, checked)}
                              aria-label={`Toggle ${question.code} visibility`}
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEdit(question)}
                              aria-label={`Edit ${question.code}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleting(question)}
                              className="text-muted-foreground hover:text-danger"
                              aria-label={`Delete ${question.code}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppable.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>

      <OnboardingQuestionEditorSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        question={editing}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deletingHasResponses ? 'Deactivate this question?' : 'Delete this question?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingHasResponses ? (
                <>
                  "{deleting?.question_text}" already has {deleting?.response_count} member answer
                  {deleting?.response_count === 1 ? '' : 's'}, so it can't be deleted. Deactivating
                  hides it from onboarding while keeping the historic answers intact.
                </>
              ) : (
                <>
                  "{deleting?.question_text}" ({deleting?.code}) has no member answers and will be
                  permanently removed, including its options. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className={
                deletingHasResponses
                  ? undefined
                  : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              }
            >
              {deletingHasResponses ? 'Deactivate' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
